import { useMutation, useQuery } from '@apollo/client';
import { faDatabase, faPlus, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';

import Button from '../../core/components/Button.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FilterDropdown from '../../core/components/Filter.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Layout from '../../core/components/Layout.tsx';
import Modal from '../../core/components/Modal.tsx';
import OrderByDropdown from '../../core/components/OrderByDropdown.tsx';
import Page from '../../core/components/Page';
import Secret from '../../core/components/Secret.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import TabButton from '../../core/components/TabButton.tsx';
import Table from '../../core/components/Table.tsx';
import TablePagination from '../../core/components/TablePagination.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { IFilter } from '../../core/utils/filterQuery.ts';
import { url } from '../../core/utils/link.ts';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';
import {
    CONCRETE_LOOKUP_TYPES,
    MAPPING_LOOKUP_TYPES,
    MASTER_DATA_ADD_PATH,
    MASTER_DATA_DETAIL_PATH,
    MASTER_DATA_PATH,
} from '../constants.ts';
import useLookupDefinitionChannelUsage, {
    LookupDefinitionChannelUsage,
} from '../hooks/useLookupDefinitionChannelUsage.ts';
import {
    CREATE_LOOKUP_DEFINITION,
    GET_LOOKUP_DEFINITIONS,
    GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES,
} from '../queries.ts';
import { MasterDataImportRun } from '../types.ts';

interface AddLookupDefinitionModalOutletContext {
    refetch: () => Promise<any>;
}

interface LookupDefinition {
    id: string;
    name?: string | null;
    lookupType: string;
    entryCount?: number | null;
}

const PAGE_PARAM = 'page';
const ITEMS_PER_PAGE = 10;
// The overview merges the definition list with one active-version import run per definition. The
// metadata service caps connection page sizes at 100 (graphene RELAY_CONNECTION_MAX_LIMIT) and
// errors above it, so this must not exceed 100 (still above realistic per-customer counts).
const MAX_ACTIVE_VERSION_RUNS = 100;

const AddIcon = withIcon(faPlus);
const ProvidedIcon = withIcon(faDatabase);
const LearnedIcon = withIcon(faSparkles);

const FORM_DEFAULT_VALUES = {
    lookupType: '',
    name: '',
};

export const AddLookupDefinitionModal = () => {
    const { t } = useTranslation('masterdata');
    const { refetch } = useOutletContext<AddLookupDefinitionModalOutletContext>();
    const { user } = useApplicationContext();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const [createLookupDefinition] = useMutation(CREATE_LOOKUP_DEFINITION);
    const handleCreate = async (formData) => {
        try {
            const { data } = await createLookupDefinition({
                variables: {
                    customerId: user?.customer?.id,
                    lookupType: formData.lookupType,
                    name: formData.name,
                },
            });
            await refetch();
            publishToast({ description: t('hub.addModal.createSuccess'), status: 'success' });

            const lookupDefinitionId = data?.createLookupDefinitionMutation?.lookupDefinition?.id;
            navigate(lookupDefinitionId ? url(MASTER_DATA_DETAIL_PATH, { lookupDefinitionId }) : url(MASTER_DATA_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('hub.addModal.createError'), status: 'error' });
        }
    };

    return (
        <Modal
            visible
            onClose={() => navigate(url(MASTER_DATA_PATH))}
            title={t('hub.addModal.title')}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(MASTER_DATA_PATH)}>{t('hub.addModal.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="lookup-definition-form">
                        {t('hub.addModal.createButton')}
                    </Button>
                </>
            }
        >
            <form
                className="flex flex-col gap-6"
                onSubmit={form.handleSubmit(handleCreate)}
                id="lookup-definition-form"
            >
                <Controller
                    name="lookupType"
                    control={form.control}
                    rules={{ required: t('hub.addModal.typeRequired') }}
                    render={({ field }) => (
                        <FormField label={t('hub.addModal.typeLabel')} required error={formErrors.lookupType}>
                            <SelectField
                                value={field.value || null}
                                onValueChange={field.onChange}
                                onBlur={field.onBlur}
                                options={CONCRETE_LOOKUP_TYPES.map((lookupType) => ({
                                    value: lookupType,
                                    label: t(`importRun.lookupType.${lookupType}`),
                                }))}
                                placeholder={t('hub.addModal.typePlaceholder')}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="name"
                    control={form.control}
                    rules={{ required: t('hub.addModal.nameRequired') }}
                    render={({ field }) => (
                        <FormField label={t('hub.addModal.nameLabel')} required error={formErrors.name}>
                            <StringField required {...field} />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

interface LookupDefinitionsTableProps {
    definitions: LookupDefinition[];
    activeVersionRunByLookupDefinitionId: Map<string, MasterDataImportRun>;
    usageByLookupDefinitionId: Map<string, LookupDefinitionChannelUsage[]>;
    isUsageLoading: boolean;
    isLoading: boolean;
    activePage: number;
    itemsPerPage: number;
    onActivePageChange: (page: number) => void;
    onItemsPerPageChange: (itemsPerPage: number) => void;
    hasImportRuns: boolean;
    emptyTitle: string;
    emptyMessage: string;
}

// cells carry their own background, so the hover color goes on the cells (group-hover), like RecordTable does
const CELL_CLASS_NAME = 'group-hover:bg-surface-strong transition-colors duration-200';

const LookupDefinitionsTable = ({
    definitions,
    activeVersionRunByLookupDefinitionId,
    usageByLookupDefinitionId,
    isUsageLoading,
    isLoading,
    activePage,
    itemsPerPage,
    onActivePageChange,
    onItemsPerPageChange,
    hasImportRuns,
    emptyTitle,
    emptyMessage,
}: LookupDefinitionsTableProps) => {
    const { t } = useTranslation('masterdata');

    const totalCount = definitions.length;
    const pageDefinitions = definitions.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);
    const numColumns = hasImportRuns ? 6 : 5;

    return (
        <>
            <Table scrollable className={classnames('w-full table-auto', isLoading && 'opacity-50')}>
                <Table.Head>
                    <Table.Row>
                        <Table.HeadCell className="min-w-96">{t('hub.overview.table.name')}</Table.HeadCell>
                        <Table.HeadCell>{t('hub.overview.table.type')}</Table.HeadCell>
                        <Table.HeadCell>{t('hub.overview.table.id')}</Table.HeadCell>
                        <Table.HeadCell className="text-right">{t('hub.overview.table.entries')}</Table.HeadCell>
                        <Table.HeadCell className="text-right">{t('hub.overview.table.channels')}</Table.HeadCell>
                        {hasImportRuns && <Table.HeadCell>{t('hub.overview.table.lastUpdate')}</Table.HeadCell>}
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {pageDefinitions.length > 0 ? (
                        pageDefinitions.map((definition) => {
                            const activeVersionRun = activeVersionRunByLookupDefinitionId.get(definition.id);
                            const numEntries = hasImportRuns
                                ? (activeVersionRun?.ingestionFileNumRows ?? activeVersionRun?.inputFileNumRows)
                                : definition.entryCount;
                            const numChannels = (usageByLookupDefinitionId.get(definition.id) || []).length;

                            return (
                                <Table.Row key={definition.id}>
                                    <Table.Cell className={CELL_CLASS_NAME}>
                                        <Link
                                            className="block w-full font-medium text-content-strong hover:text-content-strong"
                                            to={url(MASTER_DATA_DETAIL_PATH, {
                                                lookupDefinitionId: definition.id,
                                            })}
                                        >
                                            {definition.name || definition.id}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell className={classnames('whitespace-nowrap', CELL_CLASS_NAME)}>
                                        {t(`importRun.lookupType.${definition.lookupType}`)}
                                    </Table.Cell>
                                    <Table.Cell className={classnames('whitespace-nowrap', CELL_CLASS_NAME)}>
                                        <Secret value={definition.id} className="text-xs" />
                                    </Table.Cell>
                                    <Table.Cell className={classnames('whitespace-nowrap text-right', CELL_CLASS_NAME)}>
                                        {numEntries ?? '—'}
                                    </Table.Cell>
                                    <Table.Cell className={classnames('whitespace-nowrap text-right', CELL_CLASS_NAME)}>
                                        {isUsageLoading ? '—' : numChannels}
                                    </Table.Cell>
                                    {hasImportRuns && (
                                        <Table.Cell className={classnames('whitespace-nowrap', CELL_CLASS_NAME)}>
                                            {activeVersionRun?.finishedAt
                                                ? format(new Date(activeVersionRun.finishedAt), 'PPpp')
                                                : '—'}
                                        </Table.Cell>
                                    )}
                                </Table.Row>
                            );
                        })
                    ) : (
                        <Table.Row>
                            <Table.Cell colSpan={numColumns}>
                                <div className="py-5 flex flex-col gap-2">
                                    <div className="text-center font-medium text-content-medium">{emptyTitle}</div>
                                    <div className="text-center text-content-medium">{emptyMessage}</div>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            {totalCount > 0 && (
                <TablePagination
                    totalCount={totalCount}
                    activePage={activePage}
                    itemsPerPage={itemsPerPage}
                    setActivePage={onActivePageChange}
                    setItemsPerPage={onItemsPerPageChange}
                />
            )}
        </>
    );
};

const MasterDataOverviewPage = () => {
    const { t } = useTranslation('masterdata');

    const [activeTab, setActiveTab] = useSearchParamState('tab', 'provided');
    const [activePage, setActivePage] = useSearchParamState(PAGE_PARAM, '1');
    const [itemsPerPage, setItemsPerPage] = useSearchParamState('pageSize', ITEMS_PER_PAGE.toString());
    const [lookupTypeFilter, setLookupTypeFilter] = useSearchParamState('lookupType', '');
    const [channelFilter, setChannelFilter] = useSearchParamState('channel', '');
    const [orderBy, setOrderBy] = useSearchParamState('orderBy', 'name');

    const isLearnedTab = activeTab === 'learned';
    const lookupTypes: readonly string[] = isLearnedTab ? MAPPING_LOOKUP_TYPES : CONCRETE_LOOKUP_TYPES;

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setLookupTypeFilter('');
        setOrderBy('');
        setActivePage('');
    };

    const { usageByLookupDefinitionId, channels, loading: usageLoading } = useLookupDefinitionChannelUsage();

    const parsedActivePage = parseInt(activePage);
    const parsedItemsPerPage = parseInt(itemsPerPage);

    const { data, loading, refetch } = useQuery(GET_LOOKUP_DEFINITIONS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const activeVersionFilters: IFilter[] = [{ name: 'is_active_version', value: ['true'], expression: 'eq' }];
    const { data: activeVersionData } = useQuery(GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES, {
        fetchPolicy: 'cache-and-network',
        variables: { filters: activeVersionFilters, offset: 0, first: MAX_ACTIVE_VERSION_RUNS },
    });

    const activeVersionRunByLookupDefinitionId = new Map<string, MasterDataImportRun>(
        (activeVersionData?.dataImportRuns.edges || []).map(({ node }) => [node.lookupDefinitionId, node])
    );

    // mapping lookups have no imports, so they can only be ordered by name
    const orderField = isLearnedTab ? 'name' : orderBy.replace(/^-/, '');
    const orderDirection = orderBy.startsWith('-') ? -1 : 1;
    const lastUpdateTime = (definition: LookupDefinition) => {
        const finishedAt = activeVersionRunByLookupDefinitionId.get(definition.id)?.finishedAt;
        return finishedAt ? new Date(finishedAt).getTime() : 0;
    };
    const compareByName = (a: LookupDefinition, b: LookupDefinition) => (a.name || a.id).localeCompare(b.name || b.id);

    const definitions: LookupDefinition[] = (data?.lookupDefinitions || [])
        .filter((definition) => MAPPING_LOOKUP_TYPES.includes(definition.lookupType) === isLearnedTab)
        .filter((definition) => !lookupTypeFilter || definition.lookupType === lookupTypeFilter)
        .filter(
            (definition) =>
                !channelFilter ||
                (usageByLookupDefinitionId.get(definition.id) || []).some((usage) => usage.channelId === channelFilter)
        )
        .sort(
            (a, b) =>
                orderDirection *
                (orderField === 'lastUpdate'
                    ? lastUpdateTime(a) - lastUpdateTime(b) || compareByName(a, b)
                    : compareByName(a, b))
        );

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1 min-w-0">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('hub.overview.title')}</Page.HeaderTitle>

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(MASTER_DATA_ADD_PATH)}>
                                <AddIcon />
                                {t('hub.overview.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="relative p-0 flex flex-col">
                    <div className="flex w-full flex-none bg-surface-white border-b px-4 border-line-weak items-stretch">
                        <div className="flex flex-1">
                            <TabButton isActive={!isLearnedTab} onClick={() => handleTabChange('provided')}>
                                <ProvidedIcon /> {t('hub.overview.tabs.provided')}
                            </TabButton>
                            <TabButton isActive={isLearnedTab} onClick={() => handleTabChange('learned')}>
                                <LearnedIcon /> {t('hub.overview.tabs.learned')}
                            </TabButton>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 p-7">
                        <p className="text-sm text-content-medium">
                            {t(isLearnedTab ? 'hub.overview.learned.description' : 'hub.overview.provided.description')}
                        </p>

                        <div className="flex flex-wrap gap-2 justify-start items-center">
                            <FilterDropdown
                                allLabel={t('hub.overview.filters.lookupType.all')}
                                label={t('hub.overview.filters.lookupType.some')}
                                options={lookupTypes.map((lookupType) => ({
                                    value: lookupType,
                                    label: t(`importRun.lookupType.${lookupType}`),
                                }))}
                                selected={lookupTypeFilter ? [lookupTypeFilter] : []}
                                onSelectedChange={(selected) => {
                                    setLookupTypeFilter(selected[0] ?? '');
                                    setActivePage('');
                                }}
                            />

                            <FilterDropdown
                                allLabel={t('hub.overview.filters.channel.all')}
                                label={t('hub.overview.filters.channel.some')}
                                options={channels.map((channel) => ({
                                    value: channel.channelId,
                                    label: channel.channelName,
                                    groupName: channel.productKey,
                                }))}
                                renderGroupName={(groupName) => t(`hub.products.${groupName}`)}
                                selected={channelFilter ? [channelFilter] : []}
                                onSelectedChange={(selected) => {
                                    setChannelFilter(selected[0] ?? '');
                                    setActivePage('');
                                }}
                                showSearch
                            />

                            <OrderByDropdown
                                options={[
                                    { value: 'name', label: t('hub.overview.orderBy.name') },
                                    ...(isLearnedTab
                                        ? []
                                        : [
                                              {
                                                  value: 'lastUpdate',
                                                  label: t('hub.overview.orderBy.lastUpdate'),
                                                  isDate: true,
                                              },
                                          ]),
                                ]}
                                selected={orderBy}
                                defaultValue="name"
                                onSelectedChange={(selected) => {
                                    setOrderBy(selected);
                                    setActivePage('');
                                }}
                                className="ml-auto"
                            />
                        </div>

                        <LookupDefinitionsTable
                            definitions={definitions}
                            activeVersionRunByLookupDefinitionId={activeVersionRunByLookupDefinitionId}
                            usageByLookupDefinitionId={usageByLookupDefinitionId}
                            isUsageLoading={usageLoading}
                            isLoading={loading}
                            activePage={parsedActivePage}
                            itemsPerPage={parsedItemsPerPage}
                            onActivePageChange={(page) => setActivePage(page ? page.toString() : '')}
                            onItemsPerPageChange={(itemsPerPage) =>
                                setItemsPerPage(itemsPerPage ? itemsPerPage.toString() : '')
                            }
                            hasImportRuns={!isLearnedTab}
                            emptyTitle={t(
                                isLearnedTab
                                    ? 'hub.overview.learned.noData.title'
                                    : 'hub.overview.provided.noData.title'
                            )}
                            emptyMessage={t(
                                isLearnedTab
                                    ? 'hub.overview.learned.noData.message'
                                    : 'hub.overview.provided.noData.message'
                            )}
                        />
                    </div>
                </Page.Content>
            </Page>

            <Outlet context={{ refetch }} />
        </Layout>
    );
};

export default MasterDataOverviewPage;
